import * as React from "react";
import {alpha, Box, Card, Typography, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import type {ReconciliationData} from "../../../../hooks/usePayrollRun";

interface PayrollRunReconciliationProps {
    reconciliation: ReconciliationData;
    formatEUR: (value: number | null | undefined) => string;
}

interface LineItem {
    label: string;
    value: string;
    prefix?: "+" | "-";
    bold?: boolean;
}

interface ColumnDef {
    title: string;
    accentColor: string;
    lines: LineItem[];
}

function OperatorBadge({symbol}: {readonly symbol: string}): React.ReactElement {
    return (
        <Box
            sx={{
                alignSelf: "center",
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: "#1a2b4a",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "1rem",
                flexShrink: 0,
            }}
        >
            {symbol}
        </Box>
    );
}

function ReconciliationCard({title, accentColor, lines}: ColumnDef): React.ReactElement {
    const headerBg = alpha(accentColor, 0.1);
    const footerLine = lines.find((l) => l.bold);
    const bodyLines = lines.filter((l) => !l.bold);

    return (
        <Card
            sx={{
                flex: "1 1 220px",
                minWidth: 0,
                border: "1px solid #ddd",
                borderRadius: 1,
                backgroundColor: "#fffcf8",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Colored header band */}
            <Box sx={{px: 2, py: 1, backgroundColor: headerBg, textAlign: "left"}}>
                <Typography variant="subtitle2" sx={{fontWeight: 700, color: accentColor}}>
                    {title}
                </Typography>
            </Box>

            {/* Body rows */}
            <Box sx={{px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 0.75, flexGrow: 1}}>
                {bodyLines.map((line) => (
                    <Box key={line.label} sx={{display: "flex", justifyContent: "space-between", gap: 1}}>
                        <Typography variant="body2" color="text.secondary" sx={{textAlign: "left"}}>
                            {line.prefix ? `${line.prefix} ${line.label}` : line.label}
                        </Typography>
                        <Typography variant="body2" color="text.primary" fontWeight={500}>
                            {line.value}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Colored footer band with the column total */}
            {footerLine && (
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        backgroundColor: headerBg,
                        borderTop: `1px solid ${alpha(accentColor, 0.3)}`,
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                    }}
                >
                    <Typography variant="body2" sx={{fontWeight: 700, color: accentColor, textAlign: "left"}}>
                        {`= ${footerLine.label}`}
                    </Typography>
                    <Typography variant="body2" sx={{fontWeight: 700, color: accentColor}}>
                        {footerLine.value}
                    </Typography>
                </Box>
            )}
        </Card>
    );
}

export default function PayrollRunReconciliation({
    reconciliation,
    formatEUR,
}: PayrollRunReconciliationProps): React.ReactElement {
    const {t} = useTranslation();
    const theme = useTheme();
    const {earnings, employeeDeductions, otherAdjustments, employerCost} = reconciliation;

    const columns: ColumnDef[] = [
        {
            title: t('payroll.run.reconciliation.title1'),
            accentColor: theme.palette.success.main,
            lines: [
                {label: t('payroll.run.reconciliation.baseGross'), value: formatEUR(earnings.baseGross)},
                {label: t('payroll.run.reconciliation.grossAdditions'), prefix: "+", value: formatEUR(earnings.grossAdditions)},
                {label: t('payroll.run.reconciliation.grossDeductions'), prefix: "-", value: formatEUR(earnings.grossDeductions)},
                {label: t('payroll.run.reconciliation.netToGrossAdjustment'), prefix: "+", value: formatEUR(earnings.netToGrossAdjustment)},
                {label: t('payroll.run.table.header.finalGross'), bold: true, value: formatEUR(earnings.finalGross)},
            ],
        },
        {
            title: t('payroll.run.reconciliation.title2'),
            accentColor: theme.palette.error.main,
            lines: [
                {label: t('payroll.run.reconciliation.employeePension'), prefix: "-", value: formatEUR(employeeDeductions.pensionEmployee)},
                {label: t('payroll.run.summary.incomeTax'), prefix: "-", value: formatEUR(employeeDeductions.incomeTax)},
                {label: t('payroll.run.reconciliation.netBeforeOtherDeductions'), bold: true, value: formatEUR(employeeDeductions.netBeforeOtherDeductions)},
            ],
        },
        {
            title: t('payroll.run.reconciliation.title3'),
            accentColor: theme.palette.info.main,
            lines: [
                {label: t('payroll.run.reconciliation.netAdditions'), prefix: "+", value: formatEUR(otherAdjustments.netAdditions)},
                {label: t('payroll.run.reconciliation.netDeductions'), prefix: "-", value: formatEUR(otherAdjustments.netDeductions)},
                {label: t('payroll.run.reconciliation.familyInsurance'), prefix: "-", value: formatEUR(otherAdjustments.familyInsurance)},
                {label: t('payroll.run.reconciliation.voluntaryFund'), prefix: "-", value: formatEUR(otherAdjustments.voluntaryFund)},
                {label: t('payroll.run.table.header.netToBank'), bold: true, value: formatEUR(otherAdjustments.netToBank)},
            ],
        },
        {
            title: t('payroll.run.reconciliation.title4'),
            accentColor: theme.palette.warning.main,
            lines: [
                {label: t('payroll.run.table.header.finalGross'), value: formatEUR(employerCost.finalGross)},
                {label: t('payroll.run.summary.employerPension'), prefix: "+", value: formatEUR(employerCost.employerPension)},
                {label: t('payroll.run.reconciliation.employerPaidBenefits'), prefix: "+", value: formatEUR(employerCost.employerPaidBenefits)},
                {label: t('payroll.run.summary.totalEmployerCost'), bold: true, value: formatEUR(employerCost.totalEmployerCost)},
            ],
        },
    ];

    return (
        <Box sx={{width: "100%", p: {xs: 1, sm: 3}, pt: 0}}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: {xs: "column", md: "row"},
                    alignItems: "stretch",
                    gap: 2,
                }}
            >
                <ReconciliationCard {...columns[0]} />
                <OperatorBadge symbol="−"/>
                <ReconciliationCard {...columns[1]} />
                <OperatorBadge symbol="−"/>
                <ReconciliationCard {...columns[2]} />
                <OperatorBadge symbol="="/>
                <ReconciliationCard {...columns[3]} />
            </Box>
        </Box>
    );
}
